import { useState } from "react";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogFooter,
    AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { LoadingButtonContent } from "@/components/ui/loading-button-content";
import { Product } from "@/types/Products";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/contexts/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";

interface ApplyOptimalPriceDialogProps {
    product: Product;
    isOpen: boolean;
    onClose: () => void;
}

type MarketsetStatus = {
    name: string;
    markets: string[];
    marketsetIdx: number;
    isConverged: boolean;
    resetScheduled: boolean;
    newPriceScheduled: boolean;
};

export default function ApplyOptimalPriceDialog({
    product,
    isOpen,
    onClose,
}: ApplyOptimalPriceDialogProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [selectedMarketsets, setSelectedMarketsets] = useState<number[]>([]);
    const { shop } = useAuth();

    const handleApplyOptimalPrice = async () => {
        setIsLoading(true);
        try {
            // Create a copy of the optimization_progress to modify
            const updatedOptimizationProgress = {
                ...product.optimization_progress,
                marketsets: product.optimization_progress?.marketsets.map(
                    (marketset, index) => {
                        if (selectedMarketsets.includes(index)) {
                            return {
                                ...marketset,
                                reset_scheduled: true,
                                new_price_scheduled: true,
                            };
                        }
                        return marketset;
                    }
                ),
            };

            const { error } = await supabase
                .from("products")
                .update({
                    optimization_progress: updatedOptimizationProgress,
                    last_optimized: new Date().toISOString(),
                    status: "Optimized",
                })
                .eq("id", product.id);

            if (error) {
                throw error;
            }

            // Close the dialog on success
            onClose();
        } catch (error) {
            console.error("Error applying optimal price:", error);
            // You might want to add a toast notification here
        } finally {
            setIsLoading(false);
        }
    };

    const formattedOptimalPrice = new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
    }).format(product.optimization_data?.best_prices?.at(-1) ?? 0);

    const marketsetsStatus: MarketsetStatus[] =
        product.optimization_progress?.marketsets.map((marketset, index) => ({
            name: marketset.markets.join(", "),
            markets: marketset.markets,
            marketsetIdx: index,
            isConverged: marketset.is_converged,
            resetScheduled: marketset.reset_scheduled,
            newPriceScheduled: marketset.new_price_scheduled,
        })) ?? [];

    const handleMarketsetToggle = (marketsetIdx: number) => {
        setSelectedMarketsets((prev) =>
            prev.includes(marketsetIdx)
                ? prev.filter((idx) => idx !== marketsetIdx)
                : [...prev, marketsetIdx]
        );
    };

    return (
        <AlertDialog open={isOpen} onOpenChange={onClose}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Apply Optimal Price</AlertDialogTitle>
                    <AlertDialogDescription>
                        This will set the price to {formattedOptimalPrice} for{" "}
                        {product.product_name} ({product.variant_name}) and end
                        the optimization process. This action cannot be undone.
                        Note: It will take a few minutes for the price to be
                        updated on your Shopify store.
                        <div className="mt-4 space-y-3">
                            <p className="font-medium">
                                Select marketsets to update:
                            </p>
                            <div className="space-y-2">
                                {marketsetsStatus.map((marketset) => (
                                    <div
                                        key={marketset.marketsetIdx}
                                        className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-accent/50 cursor-pointer"
                                        onClick={() =>
                                            handleMarketsetToggle(
                                                marketset.marketsetIdx
                                            )
                                        }
                                    >
                                        <Checkbox
                                            checked={selectedMarketsets.includes(
                                                marketset.marketsetIdx
                                            )}
                                            onCheckedChange={() =>
                                                handleMarketsetToggle(
                                                    marketset.marketsetIdx
                                                )
                                            }
                                        />
                                        <div className="flex-1">
                                            <p className="font-medium">
                                                {marketset.name}
                                            </p>
                                            <div className="flex gap-2 mt-1">
                                                <Badge
                                                    variant={
                                                        marketset.isConverged
                                                            ? "success"
                                                            : "warning"
                                                    }
                                                >
                                                    {marketset.isConverged
                                                        ? "Converged"
                                                        : "Optimizing"}
                                                </Badge>
                                                {marketset.resetScheduled && (
                                                    <Badge variant="destructive">
                                                        Reset Scheduled
                                                    </Badge>
                                                )}
                                                {marketset.newPriceScheduled && (
                                                    <Badge variant="default">
                                                        New Price Scheduled
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <Button
                        onClick={handleApplyOptimalPrice}
                        className="w-[100px]"
                        disabled={isLoading || selectedMarketsets.length === 0}
                    >
                        <LoadingButtonContent loading={isLoading}>
                            Apply
                        </LoadingButtonContent>
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
